/* ---------------------------------------------------------------------------
 * GMM Song Flow — controller for the quiz -> wait -> preview/unlock funnel.
 *
 * Two states on one page:
 *   A (waiting)  the gmm-song-wait section shows a VSL video; every section that
 *                follows it in the DOM is hidden until the wait completes.
 *   B (preview)  the wait section is hidden and the rest of the page (offer,
 *                reviews, faq, ...) is revealed — this is the sales/unlock screen.
 *
 * There is no backend yet: getPreviewData() resolves from section config and a
 * placeholder MP3. The single seam is documented below so a real generation API
 * can be wired in without touching markup.
 *
 * Contract (data attributes the Liquid sections must render):
 *   script.gmm-config[type=application/json]  one or more; merged into `config`
 *   [data-gmm-wait][data-delay-minutes]        wait section root
 *   [data-gmm-progress]                        progress-bar fill (width %)
 *   [data-gmm-progress-pct]                    percentage text node (e.g. "48%")
 *   [data-gmm-progress-label]                  step text node (changes over time)
 *   [data-gmm-offer]                           offer section root
 *   [data-gmm-noquiz]                          "take the quiz" banner (shown w/o quizId)
 *   [data-gmm-tpl]                             text node whose content has [name] / [relationship] tokens
 *   [data-gmm-player][data-audio-url]          audio card root
 *     [data-gmm-play]                          play/pause button
 *     [data-gmm-waveform] > .gmm-wave-bar      bars (get a --played class as playback advances)
 *     [data-gmm-audio-time]                    "0:12 / 0:60" label (add data-time-current-only for "0:12" only)
 *   [data-gmm-countdown][data-minutes][data-expired-text]
 *     [data-gmm-countdown-time]                mm:ss label
 *   input[name=gmm-delivery][data-variant-id][data-price]
 *   [data-gmm-keepsake][data-variant-id]       upsell checkbox (input)
 *   [data-gmm-cta]                             unlock button(s) — may be several
 *   [data-gmm-error]                           inline error slot
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  if (window.__gmmSongFlowInit) return;
  window.__gmmSongFlowInit = true;

  var DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);

  /* ---- config merge -------------------------------------------------------- */
  function readConfig() {
    var cfg = {};
    var nodes = document.querySelectorAll('script.gmm-config[type="application/json"]');
    for (var i = 0; i < nodes.length; i++) {
      try {
        var part = JSON.parse(nodes[i].textContent || '{}');
        for (var k in part) {
          if (Object.prototype.hasOwnProperty.call(part, k)) cfg[k] = part[k];
        }
      } catch (e) { /* ignore malformed fragment */ }
    }
    return cfg;
  }

  /* ---- storage (fails soft in private mode) -------------------------------- */
  var mem = {};
  function ls(key, value) {
    if (arguments.length === 2) {
      try { window.localStorage.setItem(key, value); } catch (e) { mem[key] = value; }
      return value;
    }
    try {
      var v = window.localStorage.getItem(key);
      return v !== null ? v : (key in mem ? mem[key] : null);
    } catch (e) { return key in mem ? mem[key] : null; }
  }

  /* ---- url params ---------------------------------------------------------- */
  function params() {
    try { return new URLSearchParams(window.location.search); }
    catch (e) { return { get: function () { return null; } }; }
  }

  function escapeName(raw) {
    if (!raw) return '';
    // Collapse whitespace, cap length; used only via textContent so no markup risk.
    return String(raw).replace(/\s+/g, ' ').trim().slice(0, 60);
  }

  function fmtClock(totalSeconds) {
    var s = Math.max(0, Math.floor(totalSeconds));
    var m = Math.floor(s / 60);
    var r = s % 60;
    return m + ':' + (r < 10 ? '0' : '') + r;
  }
  function fmtCountdown(totalSeconds) {
    var s = Math.max(0, Math.floor(totalSeconds));
    var m = Math.floor(s / 60);
    var r = s % 60;
    return (m < 10 ? '0' : '') + m + ':' + (r < 10 ? '0' : '') + r;
  }

  /* =========================================================================
   * Backend seam — the ONLY place preview data is resolved.
   * Today: returns config + placeholder audio, status 'ready'.
   * Later: replace the body with
   *   const r = await fetch(config.apiBase + '/preview/' + quizId);
   *   return await r.json();  // { name, previewUrl, status, coverImage }
   * When status can be 'processing', the wait loop below already polls it.
   * ========================================================================= */
  function getPreviewData(config, quizId, name) {
    return Promise.resolve({
      quizId: quizId || null,
      name: name || config.fallbackName || '',
      previewUrl: config.placeholderAudioUrl || '',
      status: 'ready',
      coverImage: config.coverImage || ''
    });
  }

  /* ---- section visibility -------------------------------------------------- */
  function sectionWrapper(el) {
    // Walk up to the theme's .shopify-section wrapper for this element.
    while (el && el !== document.body) {
      if (el.classList && el.classList.contains('shopify-section')) return el;
      el = el.parentElement;
    }
    return null;
  }

  function followingSections(waitWrapper) {
    var out = [];
    var n = waitWrapper && waitWrapper.nextElementSibling;
    while (n) {
      // Collect funnel sections only — never the theme's footer group (rendered inside <main>).
      if (n.classList &&
          n.classList.contains('shopify-section') &&
          !n.classList.contains('shopify-section-group-footer-group')) {
        out.push(n);
      }
      n = n.nextElementSibling;
    }
    return out;
  }

  function clearStateClasses() {
    document.documentElement.classList.remove('gmm-state-wait', 'gmm-state-preview');
  }

  function setHidden(el, hidden) {
    if (!el) return;
    el.style.display = hidden ? 'none' : '';
  }

  /* ---- stop the wait video when the bar hits 100% -------------------------- */
  function stopWaitMedia(scope) {
    if (!scope) return;
    // Native <video>/<audio>: mute + pause so no sound bleeds into the offer screen.
    var media = scope.querySelectorAll('video, audio');
    for (var i = 0; i < media.length; i++) {
      try { media[i].muted = true; media[i].pause(); } catch (e) {}
    }
    // Embedded players (vturb / YouTube iframes): blank the src to fully kill playback.
    var frames = scope.querySelectorAll('iframe');
    for (var j = 0; j < frames.length; j++) {
      try { frames[j].src = 'about:blank'; } catch (e) {}
    }
  }

  /* ---- audio player -------------------------------------------------------- */
  function wirePlayer(root, url) {
    if (!root) return;
    var audioUrl = url || root.getAttribute('data-audio-url') || '';
    if (!audioUrl) return;

    var btn = root.querySelector('[data-gmm-play]');
    var timeLabel = root.querySelector('[data-gmm-audio-time]');
    var bars = root.querySelectorAll('[data-gmm-waveform] > .gmm-wave-bar');
    var audio = new Audio();
    audio.preload = 'none';
    audio.src = audioUrl;
    var ready = false;

    function paint() {
      var dur = isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0;
      var pct = dur ? (audio.currentTime / dur) * 100 : 0;
      if (bars.length) {
        var upto = Math.round((pct / 100) * bars.length);
        for (var i = 0; i < bars.length; i++) {
          bars[i].classList.toggle('gmm-wave-bar--played', i < upto);
        }
      }
      if (timeLabel) {
        timeLabel.textContent = timeLabel.hasAttribute('data-time-current-only')
          ? fmtClock(audio.currentTime)
          : fmtClock(audio.currentTime) + ' / ' + fmtClock(dur);
      }
    }

    audio.addEventListener('loadedmetadata', function () { ready = true; paint(); });
    audio.addEventListener('timeupdate', paint);
    audio.addEventListener('ended', function () {
      root.classList.remove('gmm-player--playing');
      audio.currentTime = 0;
      paint();
    });
    audio.addEventListener('error', function () {
      if (timeLabel) timeLabel.textContent = 'Preview unavailable';
    });

    if (btn) {
      btn.addEventListener('click', function () {
        if (audio.paused) {
          audio.play().then(function () {
            root.classList.add('gmm-player--playing');
          }).catch(function () { /* autoplay blocked until gesture — this IS the gesture */ });
        } else {
          audio.pause();
          root.classList.remove('gmm-player--playing');
        }
      });
    }
  }

  /* ---- countdown ----------------------------------------------------------- */
  function wireCountdown(root, quizId) {
    if (!root) return;
    var label = root.querySelector('[data-gmm-countdown-time]') || root;
    var minutes = parseFloat(root.getAttribute('data-minutes')) || 5;
    var expiredText = root.getAttribute('data-expired-text') || '';
    var key = 'gmm:song:' + (quizId || 'anon') + ':holdEnd';

    var end = parseInt(ls(key), 10);
    var now = Date.now();
    if (!end || isNaN(end) || end < now) {
      end = now + minutes * 60 * 1000;
      ls(key, String(end));
    }

    function tick() {
      var remaining = Math.floor((end - Date.now()) / 1000);
      if (remaining <= 0) {
        label.textContent = '00:00';
        if (expiredText) {
          var slot = root.querySelector('[data-gmm-countdown-expired]');
          if (slot) { slot.textContent = expiredText; slot.hidden = false; }
        }
        clearInterval(timer);
        return;
      }
      label.textContent = fmtCountdown(remaining);
    }
    tick();
    var timer = setInterval(tick, 1000);
  }

  /* ---- cart / checkout ----------------------------------------------------- */
  function currentVariantId() {
    var checked = document.querySelector('input[name="gmm-delivery"]:checked');
    if (checked) return checked.getAttribute('data-variant-id');
    // Fallback when no delivery block is present: use the offer's default variant.
    var offer = document.querySelector('[data-gmm-offer][data-default-variant]');
    return offer ? offer.getAttribute('data-default-variant') : null;
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function showError(msg) {
    var slots = document.querySelectorAll('[data-gmm-error]');
    for (var i = 0; i < slots.length; i++) {
      slots[i].textContent = msg;
      slots[i].hidden = false;
    }
  }

  function wireCta(config, quizId, name) {
    var buttons = document.querySelectorAll('[data-gmm-cta]');
    if (!buttons.length) return;

    var inFlight = false;

    function unlock() {
      if (inFlight) return;
      var variantId = currentVariantId();
      if (!variantId) { showError('Please choose a delivery option.'); return; }

      inFlight = true;
      for (var b = 0; b < buttons.length; b++) {
        buttons[b].setAttribute('aria-busy', 'true');
        buttons[b].disabled = true;
      }

      var headers = { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      var props = { 'Recipient name': name || '', 'Quiz ID': quizId || 'none', '_gmm_funnel': 'your-song' };
      var mainItem = { id: Number(variantId), quantity: 1, properties: props };

      var keepsakeItem = null;
      var keepsake = document.querySelector('[data-gmm-keepsake]');
      if (keepsake && keepsake.checked) {
        var kId = keepsake.getAttribute('data-variant-id');
        if (kId) keepsakeItem = { id: Number(kId), quantity: 1, properties: props };
      }

      var chain = Promise.resolve();
      if (config.clearCartFirst) {
        chain = chain.then(function () {
          return fetch(root() + 'cart/clear.js', { method: 'POST', headers: headers });
        });
      }

      chain.then(function () {
        // Add the song first, on its own request, so it can never be blocked by the upsell.
        return fetch(root() + 'cart/add.js', {
          method: 'POST', headers: headers, body: JSON.stringify({ items: [mainItem] })
        });
      }).then(function (res) {
        if (!res.ok) return res.json().then(function (j) { throw new Error(j.description || 'Could not add to cart.'); });
        if (keepsakeItem) {
          // Keepsake is a non-fatal upsell: a failure here still lets the song check out.
          return fetch(root() + 'cart/add.js', {
            method: 'POST', headers: headers, body: JSON.stringify({ items: [keepsakeItem] })
          }).catch(function () { /* swallow — proceed to checkout with just the song */ });
        }
      }).then(function () {
        window.location.href = root() + 'checkout';
      }).catch(function (e) {
        inFlight = false;
        for (var b = 0; b < buttons.length; b++) {
          buttons[b].removeAttribute('aria-busy');
          buttons[b].disabled = false;
        }
        showError((e && e.message) || 'Something went wrong. Please try again.');
      });
    }

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function (ev) { ev.preventDefault(); unlock(); });
    }
  }

  /* ---- delivery card selection (visual state) ------------------------------ */
  function wireDelivery() {
    var radios = document.querySelectorAll('input[name="gmm-delivery"]');
    function sync() {
      for (var i = 0; i < radios.length; i++) {
        var card = radios[i].closest('[data-gmm-delivery-card]') || radios[i].parentElement;
        if (card) card.classList.toggle('gmm-delivery-card--active', radios[i].checked);
      }
    }
    for (var i = 0; i < radios.length; i++) radios[i].addEventListener('change', sync);
    sync();
  }

  /* ---- name / relationship personalization --------------------------------- */
  function personalize(name, relationship) {
    if (!name && !relationship) return;
    var nodes = document.querySelectorAll('[data-gmm-tpl]');
    for (var i = 0; i < nodes.length; i++) {
      var tpl = nodes[i].getAttribute('data-gmm-tpl') || '';
      // textContent — never innerHTML. Tpl nodes must stay text-only (no icons/SVG inside).
      nodes[i].textContent = tpl
        .replace(/\[name\]/g, name || '')
        .replace(/\[relationship\]/g, relationship || '');
    }
  }

  /* ---- sticky CTA reveal --------------------------------------------------- */
  function wireSticky() {
    var sticky = document.querySelector('[data-gmm-sticky]');
    var anchor = document.querySelector('[data-gmm-cta-anchor]');
    if (!sticky) return;

    function reserve(show) {
      // Keep the fixed bar from covering trailing content (reviews / faq / footer).
      try { document.body.style.paddingBottom = show ? (sticky.offsetHeight + 'px') : ''; } catch (e) {}
    }

    if (!anchor || !('IntersectionObserver' in window)) {
      sticky.classList.add('gmm-sticky--visible');
      reserve(true);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      // Show the sticky bar only while the inline CTA is off-screen.
      var show = !entries[0].isIntersecting;
      sticky.classList.toggle('gmm-sticky--visible', show);
      reserve(show);
    }, { threshold: 0 });
    io.observe(anchor);
  }

  /* ---- states -------------------------------------------------------------- */
  function enterPreview(config, quizId, name, relationship, waitWrapper, following, opts) {
    // Drop the pre-paint guard classes so the inline reveal below actually takes effect.
    clearStateClasses();
    if (waitWrapper) { stopWaitMedia(waitWrapper); setHidden(waitWrapper, true); }
    for (var i = 0; i < following.length; i++) {
      setHidden(following[i], false);
      if (opts && opts.animate) {
        following[i].classList.add('gmm-fade-in');
      }
    }
    if (opts && opts.animate) {
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { window.scrollTo(0, 0); }
    }

    getPreviewData(config, quizId, name).then(function (data) {
      var finalName = escapeName(data.name) || name || config.fallbackName || '';
      if (finalName && quizId) ls('gmm:song:' + quizId + ':name', finalName);
      var finalRelationship = relationship || config.fallbackRelationship || '';
      personalize(finalName, finalRelationship);

      var player = document.querySelector('[data-gmm-player]');
      if (player) wirePlayer(player, data.previewUrl);

      wireCountdown(document.querySelector('[data-gmm-countdown]'), quizId);
      wireDelivery();
      wireCta(config, quizId, finalName);
      wireSticky();

      // No-quiz banner
      var noquiz = document.querySelector('[data-gmm-noquiz]');
      if (noquiz) noquiz.hidden = !!quizId;
    });
  }

  function enterWaiting(config, quizId, name, relationship, waitWrapper, following) {
    var de = document.documentElement;
    de.classList.add('gmm-state-wait');
    de.classList.remove('gmm-state-preview');
    setHidden(waitWrapper, false);
    for (var i = 0; i < following.length; i++) setHidden(following[i], true);

    var waitRoot = waitWrapper.querySelector('[data-gmm-wait]');
    var delayMin = (waitRoot && parseFloat(waitRoot.getAttribute('data-delay-minutes'))) || config.waitDelayMinutes || 4;
    var progress = waitWrapper.querySelector('[data-gmm-progress]');
    var pctEl    = waitWrapper.querySelector('[data-gmm-progress-pct]');
    var labelEl  = waitWrapper.querySelector('[data-gmm-progress-label]');

    // Production steps — the label swaps as the percentage climbs past each threshold.
    var steps = [
      { at: 0,  text: 'Writing the lyrics...' },
      { at: 20, text: 'Composing the music...' },
      { at: 40, text: 'Vocal recording...' },
      { at: 60, text: 'Mixing the music...' },
      { at: 82, text: 'Final check of the music...' }
    ];
    function stepText(pct) {
      var t = steps[0].text;
      for (var i = 0; i < steps.length; i++) { if (pct >= steps[i].at) t = steps[i].text; }
      return t;
    }

    // Personalize any [name] / [relationship] in the waiting copy too.
    personalize(escapeName(name) || config.fallbackName || '', relationship || config.fallbackRelationship || '');

    var key = 'gmm:song:' + (quizId || 'anon') + ':readyAt';
    var readyAt = parseInt(ls(key), 10);
    var now = Date.now();
    if (!readyAt || isNaN(readyAt)) {
      readyAt = now + delayMin * 60 * 1000;
      ls(key, String(readyAt));
    }
    var startedAt = readyAt - delayMin * 60 * 1000;

    function tick() {
      var t = Date.now();
      if (t >= readyAt) {
        clearInterval(timer);
        if (progress) progress.style.width = '100%';
        if (pctEl)    pctEl.textContent = '100%';
        if (labelEl)  labelEl.textContent = 'Your song is ready \uD83C\uDFB5';
        stopWaitMedia(waitWrapper);   // mute + stop the VSL the moment the bar reaches 100%
        enterPreview(config, quizId, name, relationship, waitWrapper, following, { animate: true });
        return;
      }
      if (progress) {
        var pct = Math.min(99, Math.max(2, ((t - startedAt) / (readyAt - startedAt)) * 100));
        progress.style.width = pct + '%';
        if (pctEl)   pctEl.textContent = Math.round(pct) + '%';
        if (labelEl) labelEl.textContent = stepText(pct);
      }
    }
    tick();
    var timer = setInterval(tick, 1000);
  }

  /* ---- init ---------------------------------------------------------------- */
  function init() {
    var config = readConfig();
    var p = params();
    var quizId = p.get('quizId');
    var skipPreview = p.get('skipPreview') === '1';
    var name = escapeName(p.get('name')) ||
               (quizId ? ls('gmm:song:' + quizId + ':name') : null) ||
               config.fallbackName || '';
    var relationship = escapeName(p.get('relationship')) ||
               (quizId ? ls('gmm:song:' + quizId + ':relationship') : null) ||
               config.fallbackRelationship || '';
    if (quizId && p.get('relationship')) ls('gmm:song:' + quizId + ':relationship', relationship);

    var waitRoot = document.querySelector('[data-gmm-wait]');
    var waitWrapper = sectionWrapper(waitRoot);
    var following = waitWrapper ? followingSections(waitWrapper) : [];

    // In the theme editor, show everything so the merchant can style both states.
    if (DESIGN_MODE) {
      clearStateClasses();
      if (waitWrapper) setHidden(waitWrapper, false);
      for (var i = 0; i < following.length; i++) setHidden(following[i], false);
      enterPreview(config, quizId, name, relationship, null, following, { animate: false });
      return;
    }

    // No quiz + redirect behavior.
    if (!quizId && config.noQuizBehavior === 'redirect' && config.quizPageUrl) {
      window.location.replace(config.quizPageUrl);
      return;
    }

    var showPreview = skipPreview || !waitWrapper;
    if (!showPreview) {
      var key = 'gmm:song:' + (quizId || 'anon') + ':readyAt';
      var readyAt = parseInt(ls(key), 10);
      if (readyAt && !isNaN(readyAt) && Date.now() >= readyAt) showPreview = true;
    }

    if (skipPreview && quizId) {
      // Lock it into preview so a refresh without the param stays in B.
      ls('gmm:song:' + quizId + ':readyAt', String(Date.now() - 1));
    }

    if (showPreview) {
      enterPreview(config, quizId, name, relationship, waitWrapper, following, { animate: false });
    } else {
      enterWaiting(config, quizId, name, relationship, waitWrapper, following);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
